import { Box } from 'src/components/UI/Box';
import { Flex } from 'src/components/UI/Flex';
import { Image } from 'src/components/UI/Image';
import { Skeleton } from 'src/components/UI/Skeleton';
import { usePrescriberContext } from 'src/providers/PrescriberProvider';
import { usePreviewStyles } from '../hooks/usePreviewStyles';

import iphoneFrame from 'src/assets/images/onboarding/onborarding-iphone.png';
import { type PlaceOfServiceValues } from '../types';

interface PrescriptionPreviewProps {
  data: PlaceOfServiceValues;
  logoPreview: string | null;
  isMobileStep?: boolean;
}

export function PrescriptionPreview({ data, logoPreview, isMobileStep = false }: PrescriptionPreviewProps) {
  const { prescriberData } = usePrescriberContext();
  const styles = usePreviewStyles();

  const doctorName = prescriberData?.fullName || data.placeName || 'Dr(a). Nome';
  const crmNumber = prescriberData?.professionalRecord?.number || '';
  const crmState = prescriberData?.professionalRecord?.state || '';
  const today = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const maxCaractersDoctorNameMobile = 14;

  const displayName =
    doctorName.length > maxCaractersDoctorNameMobile
      ? doctorName.substring(0, maxCaractersDoctorNameMobile) + '...'
      : doctorName;

  const formatCEP = (cep: string) => {
    if (!cep) return '';
    const digits = cep.replace(/\D/g, '');
    if (digits.length === 8) {
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    return cep;
  };

  return (
    <Box
      position="relative"
      w={isMobileStep ? '100%' : '380px'}
      maxW={isMobileStep ? '350px' : '380px'}
      h="auto"
      mx="auto"
    >
      <Image src={iphoneFrame} alt="iPhone Preview" w="100%" h="auto" draggable={false} />

      {/* Conteúdo sobreposto no iPhone */}
      <Box
        position="absolute"
        top="13%"
        left="7%"
        right="7%"
        bottom="10%"
        display="flex"
        flexDirection="column"
        overflowY="auto"
        height={isMobileStep ? '80%' : '100%'}
        ml={'20px'}
        mt={'40px'}
        css={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {/* Avatar e Info do Médico */}
        <Flex
          gap="12px"
          mt={styles.avatar.marginTop || '0'}
          mb={isMobileStep ? '8px' : '16px'}
          px={styles.avatar.paddingX || '8px'}
          alignItems="flex-start"
        >
          {/* Avatar com logo preview */}
          <Box
            w={isMobileStep ? '40px' : '56px'}
            h={isMobileStep ? '40px' : '56px'}
            borderRadius="12px"
            bg="gray.100"
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            flexShrink={0}
            p="4px"
          >
            {logoPreview ? (
              <Image
                src={logoPreview}
                alt="Logo"
                w="100%"
                h="auto"
                maxW="100%"
                maxH="100%"
                objectFit="contain"
                style={{ display: 'block' }}
              />
            ) : (
              <Box w="32px" h="32px" bg="gray.300" borderRadius="8px" />
            )}
          </Box>

          {/* Informações */}
          <Box flex="1">
            <Box
              fontFamily={'Ambit'}
              fontSize={styles.info.dateTextSize || '10px'}
              mt={styles.info.marginTop || '0'}
              color="gray.700"
              mb="4px"
              fontWeight={'light'}
            >
              Emitida em {today}, por
            </Box>
            <Box
              fontFamily={'Ambit'}
              fontSize={styles.info.doctorNameTextSize || '13px'}
              fontWeight="700"
              color="gray.900"
              lineHeight="1.3"
              mt={styles.info.marginTop || '0'}
              mb="2px"
            >
              Dr.(a) {isMobileStep ? displayName : doctorName}
            </Box>
          </Box>
        </Flex>

        {/* Mais Informações - Expandido */}
        <Box borderRadius="12px" mb="16px" mx="8px" mr={'28px'}>
          <Flex
            display={styles.moreInfo.separatorDisplay || 'flex'}
            justifyContent="center"
            alignItems="center"
            mt={styles.moreInfo.marginTop || '14px'}
            mb={styles.moreInfo.marginBottom || '12px'}
            gap={'14px'}
          >
            <Box fontFamily={'Ambit'} fontSize="11px" fontWeight="600" color="#5B21B6">
              Mais informações
            </Box>
            <Box fontFamily={'Ambit'} fontSize="12px" color="#5B21B6">
              ^
            </Box>
          </Flex>

          {/* Info detalhada do médico */}
          <Box
            fontFamily={'Ambit'}
            fontSize={styles.moreInfo.principalTextSize || '12px'}
            lineHeight="1.4"
            mt={styles.moreInfo.marginTop || '0'}
          >
            <Box as="span" fontFamily={'Ambit'} color="gray.800" fontWeight="500">
              Dr (a) {doctorName || data.placeName},
            </Box>
            <Box as="span" fontFamily={'Ambit'} color="gray.700" fontWeight="500">
              {' '}
              {crmNumber}/{crmState.toUpperCase()}
            </Box>
          </Box>

          {/* Info detalhada do médico */}
          {data.placeName && (
            <Box
              fontFamily={'Ambit'}
              fontSize={styles.moreInfo.principalTextSize || '12px'}
              color="gray.700"
              lineHeight="1.5"
            >
              {data.placeName} - {data.phoneNumber}
            </Box>
          )}
          <Box
            fontFamily={'Ambit'}
            fontSize={styles.moreInfo.secondaryTextSize || '11px'}
            color="gray.700"
            lineHeight="1.5"
            mb="12px"
          >
            {data.address && `${data.address}, `}
            {data.district && `${data.district}, `}
            {data.complement && `${data.complement}, `}
            {data.houseNumber && `${data.houseNumber}, `}

            {data.postalCode && `CEP ${formatCEP(data.postalCode)}`}
          </Box>
        </Box>

        <Flex
          gap="8px"
          w={'90%'}
          top={styles.validity.topDistance || '35%'}
          position={'absolute'}
          alignItems="center"
          mb="8px"
        >
          <Box
            flex="1"
            borderRadius="8px"
            p="8px 0 8px 8px"
            fontFamily={'Ambit'}
            fontSize={styles.code.codeTextSize || '9px'}
            color="gray.700"
          >
            Código Receita
          </Box>
          <Box w={styles.validity.boxSize || '29%'} borderRadius="8px" backgroundColor="gray.400" height={'10px'}></Box>
          <Box
            backgroundColor={'#F1EFFB'}
            borderRadius="8px"
            p={'2px 6px'}
            fontFamily={'Ambit'}
            fontSize={styles.validity.validityTextSize || '10px'}
            fontWeight="600"
            color="#5B21B6"
          >
            Valide na farmácia
          </Box>
        </Flex>

        <Box
          position={'absolute'}
          top={styles.information.topDistance || '42%'}
          fontFamily={'Ambit'}
          fontSize="10px"
          color="gray.700"
          lineHeight="1.4"
        >
          Sua receita <b>deve ser aceita </b>em qualquer farmácia do Brasil.
        </Box>
      </Box>

      {/* Cards de medicamentos - Skeleton placeholders */}
      <Box position={'absolute'} left={'10%'} w={'80%'} top={'83%'}>
        <Skeleton height="35px" borderRadius="10px" mb="12px" />
        <Skeleton height="40px" borderRadius="10px" />
      </Box>

      {/* Abas circulares com ícones */}
      <Flex
        position={'absolute'}
        left={isMobileStep ? '10%' : '11%'}
        w={styles.cards.width || '100%'}
        top={'78%'}
        gap="8px"
        overflowX="auto"
      >
        <Flex flexDir="row" alignItems="center" minW="68px" cursor="pointer">
          <Box
            fontFamily={'Ambit'}
            fontSize="8px"
            pr={styles.cards.medicinesRightDistance || '9%'}
            fontWeight="600"
            color="#16002C"
            textAlign="center"
          >
            Medicamentos
          </Box>
          <Box
            fontFamily={'Ambit'}
            fontSize="8px"
            pr={styles.cards.examsRightDistance || '14%'}
            color="gray.500"
            textAlign="center"
          >
            Exames
          </Box>
          <Box
            fontFamily={'Ambit'}
            fontSize="8px"
            pr={styles.cards.attestationRightDistance || '13%'}
            color="gray.500"
            textAlign="center"
          >
            Atestado
          </Box>
          <Box
            fontFamily={'Ambit'}
            fontSize="8px"
            pr={styles.cards.referralRightDistance || '10%'}
            color="gray.500"
            textAlign="center"
            whiteSpace="nowrap"
          >
            Encaminha...
          </Box>
          <Box
            fontFamily={'Ambit'}
            fontSize="8px"
            pr={styles.cards.reportRightDistance || '0'}
            color="gray.500"
            textAlign="center"
          >
            Relatóri...
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
}
